import { inject, Injectable } from '@angular/core'
import { Action, Selector, State, StateContext } from '@ngxs/store'
import { take } from 'rxjs/operators'
import { from } from 'rxjs'
import {
  DoReloadPage,
  LoadActiveAllocationCurrentUser,
  LoadEventTypes,
  LoadEventTypeSelect,
  LoadPermissions,
  LoadPermittedAllocations,
  ResetUserState,
  SetActiveAllocation,
  SetActiveAllocationCurrentUser,
  SetEventTypes,
  SetEventTypeSelect,
  SetPermissions,
  SetPermittedAllocations,
  SetSelectedNodes,
} from '../actions/user.actions'
import { MyPermission, MyPermissionService } from '../../services/my-permission/my-permission.service'
import {
  MyPermittedAllocation,
  MyPermittedAllocationsService,
} from '../../services/my-permitted-allocations/my-permitted-allocations.service'
import {
  ActiveAllocationCurrentUser,
  ActiveAllocationManagerService,
} from '../../services/active-allocation-manager/active-allocation-manager.service'
import { TreeNode } from 'src/app/components/allocation-tree-select/allocation-tree-select.component'
import { ConfigTabType } from '../../pages/settings/config-management/config-management.page'
import { EventType } from '../../models/event-type'
import { EventTypeSelect } from '../../models/EventTypeSelect'
import { FeathersService } from '../../services/feathers.service'

export interface UserStateModel {
  selectedNodes: TreeNode[]
  permissions: MyPermission[]
  permittedAllocations: MyPermittedAllocation[]
  activeAllocationCurrentUser: ActiveAllocationCurrentUser | null
  eventTypes: EventType[]
  eventTypeSelect: EventTypeSelect[]
}

@State<UserStateModel>({
  name: 'user',
  defaults: {
    selectedNodes: [],
    permissions: [],
    permittedAllocations: [],
    activeAllocationCurrentUser: null,
    eventTypes: [],
    eventTypeSelect: [],
  },
})
@Injectable()
export class UserState {
  private myPermissionService = inject(MyPermissionService)
  private myPermittedAllocationsService = inject(MyPermittedAllocationsService)
  private activeAllocationManagerService = inject(ActiveAllocationManagerService)
  private feathersService = inject(FeathersService)

  @Selector()
  static selectedNodes(state: UserStateModel): TreeNode[] {
    return state.selectedNodes
  }

  @Selector()
  static permissions(state: UserStateModel): MyPermission[] {
    return state.permissions
  }

  @Selector()
  static permittedAllocations(state: UserStateModel): MyPermittedAllocation[] {
    return state.permittedAllocations
  }

  @Selector()
  static activeAllocationCurrentUser(state: UserStateModel): ActiveAllocationCurrentUser | null {
    return state.activeAllocationCurrentUser
  }

  @Selector()
  static eventTypes(state: UserStateModel): EventType[] {
    return state.eventTypes
  }

  @Selector()
  static eventTypeSelect(state: UserStateModel): EventTypeSelect[] {
    return state.eventTypeSelect
  }

  @Selector()
  static getConfigTabsForActiveAllocation(state: UserStateModel): (ConfigTabType | string)[] {
    const tabs: (ConfigTabType | string)[] = [ConfigTabType.Obecne]
    const { activeAllocationCurrentUser, permittedAllocations } = state
    if (activeAllocationCurrentUser && permittedAllocations && permittedAllocations.length > 0) {
      const found = permittedAllocations.find(
        (pa) => pa.socialService.id === activeAllocationCurrentUser.socialServiceId,
      )
      if (found) {
        const code = Number(found.socialService.socialServiceTypeCode)
        if (code === ConfigTabType.OA && !tabs.includes(ConfigTabType.OA)) tabs.push(ConfigTabType.OA)
        if (code === ConfigTabType.PS && !tabs.includes(ConfigTabType.PS)) tabs.push(ConfigTabType.PS)
      }
    }
    return tabs
  }

  @Action(ResetUserState)
  reset(ctx: StateContext<UserStateModel>): void {
    ctx.setState({
      selectedNodes: [],
      permissions: [],
      permittedAllocations: [],
      activeAllocationCurrentUser: null,
      eventTypes: [],
      eventTypeSelect: [],
    })
  }

  @Action(SetSelectedNodes)
  setSelectedNodes(ctx: StateContext<UserStateModel>, action: SetSelectedNodes): void {
    ctx.setState({
      ...ctx.getState(),
      selectedNodes: action.selectedNodes,
    })
    ctx.dispatch(new DoReloadPage())
  }

  @Action(DoReloadPage)
  doReloadPage(ctx: StateContext<UserStateModel>): void {
    setTimeout(() => {
      window.location.reload()
    }, 1000)
  }

  @Action(SetPermissions)
  setPermissions(ctx: StateContext<UserStateModel>, action: SetPermissions): void {
    ctx.setState({
      ...ctx.getState(),
      permissions: action.permissions,
    })
  }

  @Action(LoadPermissions)
  loadPermissions(ctx: StateContext<UserStateModel>): void {
    this.myPermissionService
      .get()
      .pipe(take(1))
      .subscribe({
        next: (permissions) => {
          ctx.dispatch(new SetPermissions(permissions))
        },
        error: (error) => {
          console.error('Failed to load permissions:', error)
        },
        complete: () => {
          console.log('Permissions loading completed')
        },
      })
  }

  @Action(SetPermittedAllocations)
  setPermittedAllocations(ctx: StateContext<UserStateModel>, action: SetPermittedAllocations): void {
    ctx.setState({
      ...ctx.getState(),
      permittedAllocations: action.allocations,
    })
    setSelectedNodes(ctx)
  }

  @Action(LoadPermittedAllocations)
  loadPermittedAllocations(ctx: StateContext<UserStateModel>): void {
    this.myPermittedAllocationsService
      .get()
      .pipe(take(1))
      .subscribe({
        next: (allocations) => {
          ctx.dispatch(new SetPermittedAllocations(allocations))
        },
        error: (error) => {
          console.error('Failed to load permitted allocations:', error)
        },
      })
  }

  @Action(LoadActiveAllocationCurrentUser)
  loadActiveAllocationCurrentUser(ctx: StateContext<UserStateModel>): void {
    this.activeAllocationManagerService
      .get()
      .pipe(take(1))
      .subscribe({
        next: (result) => {
          if (result && result.length > 0) {
            ctx.dispatch(new SetActiveAllocationCurrentUser(result[0]))
          } else {
            // If no active allocation found, try to set the first allocation and location
            const { permittedAllocations } = ctx.getState()
            const firstAllocation = permittedAllocations?.[0]
            const firstLocation = firstAllocation?.locations?.[0]
            if (firstAllocation && firstLocation) {
              ctx.dispatch(
                new SetActiveAllocation({
                  socialServiceId: firstAllocation.socialService.id,
                  locationId: firstLocation.id,
                }),
              )
            } else {
              ctx.dispatch(new SetActiveAllocationCurrentUser(null))
            }
          }
        },
        error: (error) => {
          console.error('Failed to load active allocation for current user:', error)
          ctx.dispatch(new SetActiveAllocationCurrentUser(null))
        },
      })
  }

  @Action(SetActiveAllocation)
  setActiveAllocation(ctx: StateContext<UserStateModel>, action: SetActiveAllocation): void {
    this.activeAllocationManagerService
      .set(action.payload.socialServiceId, action.payload.locationId)
      .pipe(take(1))
      .subscribe({
        next: (result) => {
          ctx.dispatch(new LoadActiveAllocationCurrentUser())
        },
        error: (error) => {
          console.error('Failed to set active allocation:', error)
        },
      })
  }

  @Action(SetActiveAllocationCurrentUser)
  setActiveAllocationCurrentUser(ctx: StateContext<UserStateModel>, action: SetActiveAllocationCurrentUser): void {
    ctx.patchState({ activeAllocationCurrentUser: action.payload })
    setSelectedNodes(ctx)
  }

  @Action(SetEventTypes)
  setEventTypes(ctx: StateContext<UserStateModel>, action: SetEventTypes): void {
    ctx.patchState({ eventTypes: action.eventTypes })
  }

  @Action(LoadEventTypes)
  loadEventTypes(ctx: StateContext<UserStateModel>): void {
    from(this.feathersService.service('features/event-types').find())
      .pipe(take(1))
      .subscribe({
        next: (response: any) => {
          // Handle both response.data and direct array response
          const data = (response as any)?.data || (response as any) || []
          if (data && Array.isArray(data) && data.length > 0) {
            ctx.dispatch(new SetEventTypes(data))
          }
        },
        error: (error) => {
          console.error('Failed to load event types:', error)
        },
      })
  }

  @Action(LoadEventTypeSelect)
  loadEventTypeSelect(ctx: StateContext<UserStateModel>): void {
    from(this.feathersService.service(EventTypeSelect.serviceName).find())
      .pipe(take(1))
      .subscribe({
        next: (res: any) => {
          if (res) ctx.dispatch(new SetEventTypeSelect(res))
        },
        error: (error) => console.error('Failed to load EventTypeSelect:', error),
      })
  }

  @Action(SetEventTypeSelect)
  setEventTypeSelect(ctx: StateContext<UserStateModel>, action: SetEventTypeSelect) {
    ctx.patchState({
      eventTypeSelect: action.payload,
    })
  }

  private transformToTreeNodes(data: any[], parentLabel?: string): TreeNode[] {
    return data.map((item) => ({
      id: item.id,
      label: item.label,
      value: item.key,
      icon: item.icon,
      children: item.children ? this.transformToTreeNodes(item.children, item.label) : undefined,
      expanded: false,
      selected: false,
      parentLabel,
    }))
  }
}

function setSelectedNodes(ctx: StateContext<UserStateModel>) {
  const state = ctx.getState()
  if (state.activeAllocationCurrentUser && state.permittedAllocations && state.permittedAllocations.length > 0) {
    const allocationId = state.activeAllocationCurrentUser.allocationId
    let selectedNode: TreeNode | null = null
    for (const service of state.permittedAllocations) {
      const found = service.locations.find((loc) => loc.allocationId === allocationId)
      if (found) {
        selectedNode = {
          id: found.id,
          label: found.name,
          value: String(found.id),
          icon: 'pin',
          parentLabel: service.socialService.name,
          allocationId: found.allocationId,
        }
        break
      }
    }
    if (selectedNode) {
      ctx.patchState({ selectedNodes: [selectedNode] })
    }
  }
}
